/**
 * MODULE: platform/browser/frontend/hooks/useBrowserProviderStatus.ts
 * PURPOSE: Poll platform-service for remote Chromium provider readiness (Steel / Browserbase).
 * RECENT: 2026-06-17 [2.2.1438] — CI timer types (`number` + `window.setTimeout` / `window.setInterval`) for Frontend Validation tsc on GHA.
 * RECENT_PRIOR: 2026-06-17 [2.2.1425] — Re-poll when Steel is down; refresh on tab focus.
 */

import { useEffect, useRef, useState } from 'react';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';

export interface BrowserProviderStatus {
  ready: boolean;
  provider: 'steel' | 'browserbase' | 'unknown';
  warmupRequired: boolean;
  steelReachable?: boolean | null;
  detail?: string;
}

const DEFAULT_STATUS: BrowserProviderStatus = {
  ready: true,
  provider: 'unknown',
  warmupRequired: true,
};

export function useBrowserProviderStatus(enabled = true) {
  const [status, setStatus] = useState<BrowserProviderStatus>(DEFAULT_STATUS);
  const [isLoading, setIsLoading] = useState(enabled);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let pollTimer: number | undefined;

    const load = async () => {
      try {
        const res = await fetch(apiV1Url('/platform/browser/status'), {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = (await res.json()) as BrowserProviderStatus;
        if (!cancelled) {
          const next = {
            ready: Boolean(data.ready),
            provider: data.provider ?? 'unknown',
            warmupRequired: Boolean(data.warmupRequired),
            steelReachable: data.steelReachable,
            detail: data.detail,
          };
          setStatus(next);
          statusRef.current = next;
        }
      } catch {
        // ignore — module still shows capability gate
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const schedulePoll = () => {
      if (cancelled) return;
      const current = statusRef.current;
      const steelDown = current.steelReachable === false;
      const pollMs = steelDown || !current.ready ? 15000 : 60000;
      pollTimer = window.setTimeout(() => {
        void load().finally(schedulePoll);
      }, pollMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void load();
      }
    };

    void load().finally(schedulePoll);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);

  return { status, isLoading };
}
